import { motion } from "framer-motion";
import {
  ScrollText,
  Send,
  UserCheck,
  Brain,
  BadgeCheck,
  Database,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  FileSignature,
  QrCode,
  Hash,
} from "lucide-react";
import BrandName from "@/components/site/BrandName";
import Button from "@/components/site/Button";

/* ------------------------------------------------------------------ */
/*  Step-level UI fragments — one visual per stage of the journey     */
/* ------------------------------------------------------------------ */

function StagePolicy() {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-[#0A1424] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-[#D4A65A] font-mono">
          <ScrollText size={10} /> Policy draft
        </div>
        <span className="text-[9px] font-mono text-slate-600">v3</span>
      </div>
      <div className="space-y-1.5">
        <div className="h-1.5 rounded-full bg-white/[0.08] w-full" />
        <div className="h-1.5 rounded-full bg-white/[0.06] w-11/12" />
        <div className="h-1.5 rounded-full bg-white/[0.06] w-9/12" />
        <div className="h-1.5 rounded-full bg-white/[0.06] w-10/12" />
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-[10px] text-[#D4A65A]">
        <FileSignature size={10} /> Ready to publish
      </div>
    </div>
  );
}

function StageDistribute() {
  const rows = [
    ["Nurse team", "12"],
    ["Dispensing", "9"],
    ["Reception", "6"],
    ["Night shift", "15"],
  ];
  return (
    <div className="rounded-lg border border-white/[0.07] bg-[#0A1424] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-[#D4A65A] font-mono">
          <Send size={10} /> Assign by role
        </div>
        <span className="text-[9px] font-mono text-slate-600">42 people</span>
      </div>
      <div className="space-y-1">
        {rows.map(([r, n]) => (
          <div
            key={r}
            className="flex items-center justify-between rounded bg-white/[0.02] px-2 py-1.5"
          >
            <span className="text-[10.5px] text-slate-300">{r}</span>
            <span className="text-[9.5px] font-mono text-[#D4A65A]">{n}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StageAcknowledge() {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-[#0A1424] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-[#D4A65A] font-mono">
          <UserCheck size={10} /> Signed by
        </div>
        <span className="text-[9px] font-mono text-slate-500">10:24 GMT</span>
      </div>
      <div className="flex items-center gap-2.5">
        <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-[#D4A65A]/40 to-[#D4A65A]/10 border border-[#D4A65A]/30 flex items-center justify-center text-[10px] font-semibold text-[#D4A65A]">
          RA
        </div>
        <div className="min-w-0">
          <div className="text-white text-[11.5px] font-semibold truncate">
            Dr. Rania Ahmed
          </div>
          <div className="text-[9.5px] font-mono text-slate-500">
            ID verified &middot; MFA
          </div>
        </div>
      </div>
      <div className="mt-2.5 rounded bg-white/[0.02] border border-white/[0.05] px-2 py-1.5 flex items-center gap-1.5">
        <div className="flex-1 h-[18px] flex items-center">
          <svg viewBox="0 0 120 18" className="w-full h-full text-[#D4A65A]/80">
            <path
              d="M2 12 Q10 2 18 10 T34 10 Q42 18 50 8 T70 10 Q80 4 90 12 T112 10"
              stroke="currentColor"
              strokeWidth="1.4"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <CheckCircle2 size={11} className="text-emerald-300 shrink-0" />
      </div>
    </div>
  );
}

function StageComprehend() {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-[#0A1424] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-[#D4A65A] font-mono">
          <Brain size={10} /> Adaptive check
        </div>
        <span className="text-[9px] font-mono text-emerald-300">96%</span>
      </div>
      <div className="space-y-1.5">
        {[
          ["Storage temperature range", true],
          ["Chain-of-custody sign-off", true],
          ["Adverse event reporting", true],
          ["Prescription vs consumer route", false],
        ].map(([q, ok]) => (
          <div
            key={q}
            className="flex items-center gap-2 rounded bg-white/[0.02] px-2 py-1"
          >
            <span
              className={`h-3.5 w-3.5 rounded-full flex items-center justify-center ${
                ok
                  ? "bg-emerald-500/15 text-emerald-300"
                  : "bg-amber-500/15 text-amber-300"
              }`}
            >
              {ok ? "✓" : "→"}
            </span>
            <span className="text-[10.5px] text-slate-300 truncate">{q}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StageCertify() {
  return (
    <div className="rounded-lg border border-emerald-400/25 bg-gradient-to-br from-[#0A1F1B] to-[#0A1424] p-3 relative overflow-hidden">
      <div className="absolute -top-10 -right-10 h-24 w-24 rounded-full bg-emerald-500/[0.15] blur-2xl" />
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-emerald-300 font-mono">
            <BadgeCheck size={10} /> Certificate
          </div>
          <span className="text-[9px] font-mono text-emerald-300/80">
            #A7F2-91C
          </span>
        </div>
        <div className="text-white text-[11px] font-semibold tracking-tight">
          SOP-CB-14 Handling
        </div>
        <div className="text-[9.5px] text-slate-400 mt-0.5">
          Issued to R. Ahmed
        </div>
        <div className="mt-2.5 flex items-center gap-2">
          <div className="h-9 w-9 rounded bg-white/[0.05] border border-white/[0.08] flex items-center justify-center">
            <QrCode size={14} className="text-emerald-300" />
          </div>
          <div className="text-[9.5px] font-mono text-slate-500 leading-tight">
            Verifiable at
            <br />
            <span className="text-emerald-300">/verify/A7F2-91C</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StageAudit() {
  const rows = [
    ["09:14", "Distributed", "gold"],
    ["10:24", "Acknowledged", "emerald"],
    ["10:26", "Comprehension 96%", "emerald"],
    ["10:26", "Certificate minted", "emerald"],
    ["10:26", "Chained #A7F2-91C", "emerald"],
  ];
  return (
    <div className="rounded-lg border border-white/[0.07] bg-[#0A1424] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-[#D4A65A] font-mono">
          <Database size={10} /> Audit trail
        </div>
        <span className="text-[9px] font-mono text-emerald-300 inline-flex items-center gap-1">
          <Hash size={9} />
          SHA-256
        </span>
      </div>
      <ol className="relative pl-2.5 space-y-1.5 border-l border-white/[0.06]">
        {rows.map(([t, label, tone], i) => (
          <li key={i} className="relative pl-2.5">
            <span
              className={`absolute -left-[5px] top-1 h-1.5 w-1.5 rounded-full ${
                tone === "emerald" ? "bg-emerald-400" : "bg-[#D4A65A]"
              }`}
            />
            <div className="flex items-center justify-between text-[10px]">
              <span
                className={
                  tone === "emerald" ? "text-emerald-300" : "text-[#D4A65A]"
                }
              >
                {label}
              </span>
              <span className="font-mono text-slate-600">{t}</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Journey definition                                                */
/* ------------------------------------------------------------------ */

const steps = [
  {
    icon: ScrollText,
    n: "01",
    stage: "Publish",
    title: "A policy update is issued.",
    body: "Draft in-platform or pull directly from a regulator digest. Assign to roles.",
    tone: "gold",
    fragment: StagePolicy,
  },
  {
    icon: Send,
    n: "02",
    stage: "Distribute",
    title: "Every recipient is notified.",
    body: "Signed in-app and email delivery. No inbox chase, no missed staff.",
    tone: "gold",
    fragment: StageDistribute,
  },
  {
    icon: UserCheck,
    n: "03",
    stage: "Acknowledge",
    title: "The individual signs.",
    body: "Identity verified. Optional signature capture. Time and location logged.",
    tone: "gold",
    fragment: StageAcknowledge,
  },
  {
    icon: Brain,
    n: "04",
    stage: "Comprehend",
    title: "Understanding is proven.",
    body: "Adaptive knowledge check tailored to role. Not a click, a comprehension score.",
    tone: "emerald",
    fragment: StageComprehend,
  },
  {
    icon: BadgeCheck,
    n: "05",
    stage: "Certify",
    title: "A signed certificate is minted.",
    body: "Unique verification ID. QR verifiable by any inspector, anywhere.",
    tone: "emerald",
    fragment: StageCertify,
  },
  {
    icon: Database,
    n: "06",
    stage: "Retain",
    title: "The event becomes permanent.",
    body: "Chained to an immutable audit log. Inspection ready without preparation.",
    tone: "emerald",
    fragment: StageAudit,
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function Process() {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative py-14 md:py-28 border-t border-white/[0.04] overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none grid-lines opacity-25" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="eyebrow">Watch compliance happen</div>
            <h2 className="mt-3 font-display text-4xl md:text-[52px] tracking-[-0.04em] text-white leading-[1] font-semibold">
              One policy update.<br />
              <span className="text-[#D4A65A]">
                Followed to a permanent record.
              </span>
            </h2>
          </div>
          <p className="max-w-sm text-slate-400 leading-relaxed text-[15px]">
            Six connected stages, one continuous flow inside <BrandName />. Each
            step produces its own signed artefact so the audit assembles itself.
          </p>
        </div>

        {/* Timeline meta bar */}
        <div className="grid grid-cols-2 mb-6 text-[10px] uppercase tracking-[0.22em]">
          <div className="flex items-center gap-3 text-slate-400">
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4A65A]" />
            Day 0 &middot; Update issued
          </div>
          <div className="flex items-center justify-end gap-3 text-emerald-300/90">
            Year 7 &middot; Still verifiable
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </div>
        </div>

        {/* Journey rail with UI fragments */}
        <div className="relative">
          {/* Desktop connecting rail */}
          <div className="hidden lg:block absolute left-0 right-0 top-[70px] h-px pointer-events-none">
            <div className="relative h-px w-full bg-gradient-to-r from-[#D4A65A]/70 via-white/10 to-emerald-400/70">
              <motion.span
                initial={{ opacity: 0.4 }}
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 2.4, repeat: Infinity }}
                className="absolute -top-[3px] left-0 h-1.5 w-1.5 rounded-full bg-[#D4A65A] shadow-[0_0_18px_rgba(212,166,90,0.7)]"
              />
              <motion.span
                initial={{ opacity: 0.4 }}
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 2.4, repeat: Infinity, delay: 1.2 }}
                className="absolute -top-[3px] right-0 h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.7)]"
              />
            </div>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-x-3 gap-y-12">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const Fragment = s.fragment;
              const ring =
                s.tone === "emerald"
                  ? "border-emerald-400/40 bg-emerald-500/[0.08] text-emerald-300"
                  : "border-[#D4A65A]/40 bg-[#D4A65A]/[0.08] text-[#D4A65A]";
              return (
                <motion.li
                  key={s.stage}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="relative"
                >
                  {/* Node */}
                  <div className="relative flex items-center justify-center h-14 mb-5">
                    <div
                      className={`relative h-14 w-14 rounded-full border ${ring} flex items-center justify-center shadow-[0_10px_30px_-12px_rgba(212,166,90,0.4)] z-10 bg-[#050B18]`}
                    >
                      <Icon size={18} />
                      <span
                        className={`absolute -bottom-1.5 -right-1.5 text-[9px] font-mono font-semibold px-1.5 rounded bg-[#040914] ${
                          s.tone === "emerald"
                            ? "text-emerald-300"
                            : "text-[#D4A65A]"
                        }`}
                      >
                        {s.n}
                      </span>
                    </div>
                  </div>

                  {/* UI fragment for this step — the visual proof */}
                  <div className="mb-3">
                    <Fragment />
                  </div>

                  {/* Text block */}
                  <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.03] transition-colors p-3.5">
                    <div className="text-[9.5px] uppercase tracking-[0.22em] text-slate-500 font-mono">
                      {s.stage}
                    </div>
                    <h3 className="mt-1.5 text-white text-[13.5px] font-semibold tracking-[-0.015em] leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-1.5 text-slate-400 text-[11.5px] leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>

        {/* Outcome strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.55 }}
          className="mt-16 rounded-2xl border border-white/[0.08] bg-gradient-to-r from-[#0B1424] via-[#080F1E] to-[#0B1424] p-6 md:p-8"
        >
          <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="md:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-semibold">
                What you are left with
              </div>
              <h3 className="mt-3 text-white text-xl md:text-2xl font-semibold tracking-[-0.015em] leading-snug">
                A permanent, audit ready record for every member of your
                workforce.
              </h3>
              <div className="mt-4 inline-flex items-center gap-2 text-[11px] text-emerald-300/90">
                <ShieldCheck size={12} />
                Verifiable by any inspector, from any device, at any time.
              </div>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-3">
              {[
                ["Immutable event log", "SHA-256 chained"],
                ["Signed certificate", "PDF and QR"],
                ["Comprehension score", "Per person, per module"],
                ["Regulator export", "CSV and JSON"],
              ].map(([a, b]) => (
                <div
                  key={a}
                  className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  <div className="flex-1">
                    <div className="text-white text-[13px] font-semibold tracking-tight">
                      {a}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {b}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-3">
            <div className="text-[12px] text-slate-400">
              Every step above happens inside <BrandName />, without an
              inspector needing to ask.
            </div>
            <Button
              as="a"
              href="#tryit"
              data-testid="process-try-cta"
              variant="primary"
              size="sm"
            >
              See it live
              <ArrowRight size={12} />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
